import { inject, Injectable } from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { filter, first } from 'rxjs/operators'

import { ThemeService } from '@hyperkit'

import { VerityEnvironmentService } from '../../../../environments/verity-environment.service'
import { AuthService } from '../../../auth/auth.service'

import { FrameDetectionService } from './frame-detection.service'

/**
 * Service that synchronizes the current iframe URL with the parent app (Verity)
 * by broadcasting URL changes to the parent on navigation end.
 */
@Injectable({
  providedIn: 'root',
})
export class UrlSynchronizationService {
  private authService = inject(AuthService)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  private router = inject(Router)
  private frameDetectionService = inject(FrameDetectionService)
  private themeService = inject(ThemeService)

  public targetUrl: string
  public allowedOrigins =
    this.verityEnvironmentService.verityConfig.allowedOrigins || [] // Define allowed origins for postMessage communication
  private boundMessageHandler = this.handleMessage.bind(this)

  public initialize() {
    this.authService.authState$
      .pipe(
        filter(
          (state) => state.initialized && this.authService.isAuthenticated(),
        ),
        first(),
      )
      .subscribe(() => {
        this.sendHunterReadyMessage()
        this.startListeningForMessages()
      })
  }

  private sendHunterReadyMessage() {
    this.allowedOrigins.forEach((target) => {
      window.parent.postMessage({ type: 'hunterReady' }, target)
    })
  }

  private startListeningForMessages() {
    window.addEventListener('message', this.boundMessageHandler, false)
  }

  private handleMessage(event: MessageEvent) {
    if (!this.allowedOrigins.includes(event.origin)) {
      console.warn(
        'Hunter: Received message from untrusted source:',
        event.origin,
      )
      return
    }

    switch (event.data?.type) {
      case 'initialize':
        this.targetInitialization(event)
        break

      case 'theme':
        this.updateTheme(event)
        break

      default:
        console.warn('Hunter: Unknown message type received:', event.data?.type)
    }
  }

  /**
   * Initialize target url to be used for broadcasting sent by Verity via postMessage
   */
  public targetInitialization(event: MessageEvent): void {
    if (event.data.target) {
      const { target } = event.data
      this.targetUrl = target
    }
  }

  /**
   * Initialize/update theme sent by Verity via postMessage
   */
  public updateTheme(event: MessageEvent): void {
    if (event.data.value) {
      let { value } = event.data

      if (!this.themeService.isValidTheme(value)) {
        console.warn(
          `Hunter: Invalid theme target received: '${value}'. Defaulting to 'dark'.`,
        )
        value = 'dark'
      }
      this.themeService.setTheme(value)
    }
  }

  public stopListeningForMessages() {
    window.removeEventListener('message', this.boundMessageHandler)
  }

  /**
   * Listens for Angular navigation events and broadcasts the updated URL to Verity.
   */
  public listenToRouterEvents(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.broadcastUrl(event.urlAfterRedirects)
      }
    })
  }

  /**
   * Sends the updated URL to the parent window (Verity) to synchronize the URL.
   * @param url - The current URL to broadcast.
   */
  private broadcastUrl(url: string): void {
    if (this.targetUrl) {
      window.parent.postMessage({ type: 'urlChanged', url }, this.targetUrl)
    } else {
      // If the target url didn't get set, send broadcasts to all environments
      this.allowedOrigins.forEach((target) => {
        window.parent.postMessage({ type: 'urlChanged', url }, target)
      })
    }
  }
}
